/**
 * 外注見積の取込と突合（047・048、docs/33、#81・#83。1回目＋使ってみての直し）。試作 v1（screen_sample/現場力_外注見積の突合_案_v1.html）を本実装に。
 *   048（docs/33 §8）：原本は <iframe>（CSP の object-src 'none' が <object> を止める。#87 の3回目で右の枠を無くし、見積の紙と同じ右からの引き出し VendorOriginal に）／手で打った仕入れが変わる行は「8,250 → 22,000」と両方見せ「手で打った値のまま」を選べる／
 *   明細に当たらない行は 当てた行（その行の仕入れに入る）と 当たっていない行（「この行に当てる」、配分の元手）に分け、配分の既定は OFF／
 *   ・一覧は #198 から pages/vendorQuotes/List.tsx（案件をまたいで見る。案件の画面のタブは同じ一覧を案件で絞って開く）
 *   ・登録する（/vendor-quotes/new。#198 から案件は任意＝?group=。見積依頼の画面から ?rfq=）：ファイルを選ぶ／写真を撮る → 元（PDF・写真・FAX）→ 案件 → 外注先 → 「AI読み取り開始」（非同期。ホームに出る）
 *   ・外注見積の中身（/vendor-quotes/:id）は #198 の続き（docs/79 §13）で詳細の画面 pages/vendorQuotes/Detail.tsx に移した（一覧の右側パネルと1つに）。
 *     このファイルに残るのは登録の画面だけ
 *   画面の言葉：「登録する」（読ませる操作の名前）・「AI読み取り」・「取込」（2026-10-11 夜の射場さんの決定で「AI」を名乗る。R3。それまでは「画面では AI と言わない」）
 *   9/29（#92、docs/35）：使い方の文は見出しの「？」へ。待ち時間の目安（2ページで 2〜3分）は残す（決め7）。外注タブの空のときは1文と「登録する」（決め6）
 *   #178（10/13）：手入力の税抜合計は自動保存の共通の仕組み（lib/saveQueue.tsx）。打つたびには送らず 0.5 秒後に1本ずつ（前は1文字ごとに PUT が重なっていた）。
 *     頭の欄（外注先・番号・日付・税）の保存も同じ「送っている最中」の印を通す（重ねない）。送れていない合計があるまま離れるときは確かめる
 */
import {
  VENDOR_QUOTE_ACCEPT,
  VENDOR_QUOTE_CONTENT_TYPES,
  VENDOR_QUOTE_MAX_BYTES,
  VENDOR_QUOTE_MSG,
  RECEIVED_MSG,
  type GroupList,
  type PartnerList,
  type VendorQuote,
} from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, apiUpload, messageOf } from "../api.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { FileDrop } from "../components/FileDrop.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { useLabels } from "../labels.js";

/* ---------------------------------------------------------------- 登録する */
export function VendorQuoteNewPage() {
  const [sp] = useSearchParams();
  // #198：案件は任意（届いた FAX を先に登録し、案件は後から。docs/79 決定3）。見積依頼の画面から ?rfq=（{vendor}・案件は依頼から）
  const rfqId = sp.get("rfq") ?? "";
  const [groupId, setGroupId] = useState(sp.get("group") ?? "");
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { node } = useToast();
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all"), enabled: !rfqId });
  const vendors = useQuery({ queryKey: ["partners", "", "active", "vendor"], queryFn: () => api<PartnerList>("GET", "/partners?type=vendor") });
  const [file, setFile] = useState<File | null>(null);
  const [vendorId, setVendorId] = useState("");
  const listTo = `/vendor-quotes${sp.get("group") ? `?group=${sp.get("group")}&via=group` : ""}`;
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ {vendorQuote}の一覧。ファイルを選んでいたら確かめる
  const back = useBackNav({ to: listTo, label: L.vendorQuote }, !!file);
  usePageName(`${L.vendorQuote}登録`); // #198：開くボタン（一覧の「外注見積登録」）と同じ言葉（#238）
  const [err, setErr] = useState<string | undefined>();
  const [source, setSource] = useState<"upload" | "mobile_camera" | "fax">("upload");

  const pick = (f: File | null, src: "upload" | "mobile_camera") => {
    setErr(undefined);
    if (!f) return;
    if (!(VENDOR_QUOTE_CONTENT_TYPES as readonly string[]).includes(f.type) && !/\.(pdf|jpe?g|png)$/i.test(f.name)) {
      setErr(VENDOR_QUOTE_MSG.type);
      return;
    }
    if (f.size > VENDOR_QUOTE_MAX_BYTES) {
      setErr(VENDOR_QUOTE_MSG.tooBig);
      return;
    }
    setFile(f);
    setSource((s) => (src === "mobile_camera" ? "mobile_camera" : s === "fax" ? "fax" : "upload"));
  };
  const read = useMutation({
    mutationFn: async () => {
      const f = file!;
      const ct = f.type || (/\.pdf$/i.test(f.name) ? "application/pdf" : /\.png$/i.test(f.name) ? "image/png" : "image/jpeg");
      const qs = new URLSearchParams({ source, name: f.name.slice(0, 200) });
      if (rfqId) qs.set("rfqId", rfqId);
      else {
        if (groupId) qs.set("groupId", groupId);
        if (vendorId) qs.set("vendorId", vendorId);
      }
      return apiUpload<VendorQuote & { message: string }>(`/vendor-quotes?${qs.toString()}`, f, ct);
    },
    onSuccess: (q) => {
      void qc.invalidateQueries({ queryKey: ["vendor-quotes"] });
      void qc.invalidateQueries({ queryKey: ["vql"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      nav(listTo, { state: { notice: q.message } });
    },
    onError: (e) => setErr(messageOf(e)),
  });
  return (
    <>
      <BackButton back={back} />
      {back.dialog}
      <div className="hrow">
        <h1 style={{ margin: 0 }}>
          {L.vendorQuote}登録
          <Help testId="help-vq-import" text={`${L.vendor}からもらった見積（PDF・写真）を登録すると、AI が読み取ります。写真は明るい所で、紙全体が入るように撮ります。AI読み取りには 2ページで 2〜3分かかり（ページが多いほど長く）、終わると「本日の業務」に出ます。見積の紙の社内用の列の「取込」を押すと、読み取った行を選んで見積の行にできます。`} />
        </h1>
      </div>
      <div className="vqsteps">
        <section className="paper vqstep">
          <h2><i>1</i>見積のファイル</h2>
          <div className="vqfile" data-testid="vq-file">
            {file ? (
              <>
                <span className="num">{file.name}</span>
                <span className="muted">{Math.round(file.size / 1024).toLocaleString("ja-JP")} KB</span>
                <button type="button" className="btn text" onClick={() => setFile(null)}>取消</button>
              </>
            ) : (
              // ../eigyouryoku/docs/13 決めごと13：落としても登録できる（FileDrop。1ファイル）。「撮影」はスマホのカメラ（mobile_camera）
              <FileDrop
                onFiles={(files, src) => pick(files[0] ?? null, src === "mobile_camera" ? "mobile_camera" : "upload")}
                accept={VENDOR_QUOTE_ACCEPT}
                phoneAccept={VENDOR_QUOTE_ACCEPT}
                multiple={false}
                hint={`${RECEIVED_MSG.dropHintVendor}　10ページ・20MB まで`}
                chooseLabel="ファイル選択（PDF・JPEG・PNG）"
                testId="vq-drop"
                inputTestId="vq-file-input"
                camera={{ label: "撮影", testId: "vq-camera-input" }}
              />
            )}
          </div>
        </section>
        <section className="paper vqstep">
          <h2><i>2</i>元・{L.project}</h2>
          <div className="row">
            <select className="inp" style={{ maxWidth: 220 }} value={source} onChange={(e) => setSource(e.target.value as typeof source)} aria-label="元" data-testid="vq-source">
              <option value="upload">PDF・画像</option>
              <option value="mobile_camera">写真（スマホ）</option>
              <option value="fax">FAX</option>
            </select>
            {rfqId ? (
              <span className="hint" data-testid="vq-from-rfq">見積依頼から登録します（{L.vendor}・{L.project}は依頼のもの）</span>
            ) : (
              <select className="inp" style={{ maxWidth: 380 }} value={groupId} onChange={(e) => setGroupId(e.target.value)} aria-label={L.project} data-testid="vq-group">
                <option value="">{L.project}は後で設定</option>
                {(groups.data?.items ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
          </div>
        </section>
        <section className="paper vqstep" hidden={!!rfqId}>
          <h2><i>3</i>{L.vendor}</h2>
          <div className="row">
            <select className="inp" style={{ maxWidth: 380 }} value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label={L.vendor} data-testid="vq-vendor">
              <option value="">あとで選ぶ（読み取った会社名から候補を出します）</option>
              {(vendors.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.partnerType === "both" ? `（${L.customer}でもある）` : ""}</option>
              ))}
            </select>
            <Link className="btn text" to="/partners/new?type=vendor">{L.vendor}として登録</Link>
          </div>
        </section>
        <ErrorText>{err}</ErrorText>
        <div className="row">
          <button type="button" className="btn primary" disabled={!file || read.isPending} onClick={() => read.mutate()} data-testid="vq-read">AI読み取り開始</button>
          <Link className="btn quiet" to={back.to} onClick={back.request}>取消</Link>
          <span className="hint">AI読み取りには 2ページで 2〜3分かかります（ページが多いほど長く）。</span>
        </div>
      </div>
      {node}
    </>
  );
}
